import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { date as fmtDate, hours as fmtHours, isoDate, num, pct } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/ui/StatTile';
import { StatusChip } from '@/components/ui/StatusChip';
import { ragTone } from '@/components/ui/statusTone';
import { ExportBar } from '@/components/ui/ExportBar';
import { figures } from '@/lib/export';
import type { MachineUtilisation } from '@/types/models';

interface DailyRow {
  diary_date: string;
  engine_hours: number | null;
  productive_hours: number | null;
  standby_hours: number | null;
  unplanned_downtime_hours: number | null;
  planned_downtime_hours: number | null;
  weather_hours: number | null;
  availability_pct: number | null;
  utilisation_pct: number | null;
  loads: number | null;
  tonnes_effective: number | null;
}

interface Performance {
  machine: { fleet_no: string; current_hour_meter: number } | null;
  period: { from: string; to: string };
  totals: {
    engine_hours: number;
    productive_hours: number;
    standby_hours: number;
    breakdown_hours: number;
    planned_hours: number;
    weather_hours: number;
    availability_pct: number | null;
    utilisation_pct: number | null;
    loads: number;
    tonnes: number;
    breakdown_count: number;
    avg_breakdown_hours: number | null;
  };
  daily: DailyRow[];
  /** On the machine's own rule (0044): hours for plant, days for vehicles. */
  utilisation?: (MachineUtilisation & { period: { measured_to: string } }) | null;
  service: {
    hours_remaining: number | null;
    next_service_hours: number | null;
    last_service_date: string | null;
    status_colour: string;
    meter_unit?: 'hours' | 'km';
  } | null;
  liners: Array<{
    component_name: string | null;
    description: string;
    installed_on: string;
    removed_on: string | null;
    life_hours: number | null;
    expected_life_hours: number | null;
    is_current: boolean;
    hours_in_service: number | null;
  }>;
}

const RANGES = [7, 30, 90];

/**
 * The drill-down behind "how is this machine performing?". Every figure comes
 * from machine_performance() — one call, computed in Postgres, so this panel
 * and the fleet dashboard can never disagree about a machine's availability.
 */
export function MachinePerformance({ machineId }: { machineId: string }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Performance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const from = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return isoDate(d);
  }, [days]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data: res, error: err } = await supabase.rpc('machine_performance', {
        p_machine_id: machineId,
        p_from: from,
        p_to: isoDate(),
      });
      if (cancelled) return;
      if (err) setError(err.message);
      else { setData(res as Performance); setError(null); }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [machineId, from]);

  if (loading && !data) {
    return <div className="centered" style={{ padding: 'var(--sp-6)' }}><span className="spinner" /></div>;
  }
  if (error) {
    return <p className="small" style={{ color: 'var(--rag-red)' }}>{error}</p>;
  }
  if (!data) return null;

  const t = data.totals;
  const u = data.utilisation ?? null;
  const recent = [...data.daily].reverse().slice(0, 14);
  const currentLiners = data.liners.filter((l) => l.is_current);

  return (
    <>
      <div className="row" style={{ gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
        {RANGES.map((r) => (
          <Button key={r} variant={days === r ? 'primary' : 'secondary'} onClick={() => setDays(r)}>
            {r} days
          </Button>
        ))}
        <span className="spacer" />
        <span className="small muted">
          {fmtDate(data.period.from)} – {fmtDate(data.period.to)}
        </span>
      </div>
      <div className="row" style={{ marginBottom: 'var(--sp-3)' }}>
        <ExportBar drawer title={`Machine performance · ${data.machine?.fleet_no ?? ''}`}
          subtitle={`${fmtDate(data.period.from)} – ${fmtDate(data.period.to)} (${days} days)`}
          file={`performance-${data.machine?.fleet_no ?? 'machine'}-${days}-days`}
          sheets={() => [
            { name: 'Summary', rows: figures([
              ['Machine', data.machine?.fleet_no], ['From', data.period.from], ['To', data.period.to],
              ['Availability', t.availability_pct, '%'], ['Utilisation (diaries)', t.utilisation_pct, '%'],
              [u?.class === 'vehicle' ? 'Fleet utilisation' : 'Plant utilisation', u?.utilisation_pct, '%'],
              ['Worked', u ? (u.class === 'vehicle' ? u.worked_days : u.used_hours) : null, u?.class === 'vehicle' ? 'days' : 'h'],
              ['Available', u ? (u.class === 'vehicle' ? u.available_days : u.available_hours) : null, u?.class === 'vehicle' ? 'days' : 'h'],
              ['Km driven', u?.class === 'vehicle' ? u.distance_km : null, 'km'],
              ['Engine hours', t.engine_hours, 'h'], ['Productive', t.productive_hours, 'h'], ['Standby', t.standby_hours, 'h'],
              ['Breakdown', t.breakdown_hours, 'h'], ['Planned', t.planned_hours, 'h'], ['Weather', t.weather_hours, 'h'],
              ['Tonnes moved', t.tonnes, 't'], ['Loads', t.loads], ['Breakdowns', t.breakdown_count],
              ['Average breakdown', t.avg_breakdown_hours, 'h'],
              ['Service: remaining', data.service?.hours_remaining, data.service?.meter_unit === 'km' ? 'km' : 'h'],
              ['Service: next at', data.service?.next_service_hours], ['Service: last', data.service?.last_service_date],
            ]) },
            { name: 'Daily', rows: data.daily.map((d) => ({
              Date: d.diary_date, 'Engine h': d.engine_hours, 'Productive h': d.productive_hours, 'Standby h': d.standby_hours,
              'Breakdown h': d.unplanned_downtime_hours, 'Planned h': d.planned_downtime_hours, 'Weather h': d.weather_hours,
              'Availability %': d.availability_pct, 'Utilisation %': d.utilisation_pct, Loads: d.loads, Tonnes: d.tonnes_effective,
            })) },
            { name: 'Liners', rows: data.liners.map((l) => ({
              Component: l.component_name ?? '', Liner: l.description, Installed: l.installed_on, Removed: l.removed_on ?? '',
              'Life h': l.life_hours ?? '', 'Expected life h': l.expected_life_hours ?? '', 'Hours in service': l.hours_in_service ?? '',
              Current: l.is_current ? 'yes' : '',
            })) },
          ]} />
      </div>

      <div className="grid grid--stats">
        <StatTile label="Availability" value={t.availability_pct} unit="%" decimals={1}
          rag={t.availability_pct == null ? 'grey'
            : t.availability_pct >= 85 ? 'green' : t.availability_pct >= 70 ? 'amber' : 'red'} />
        {u ? (
          <>
            <StatTile label={u.class === 'vehicle' ? 'Fleet utilisation' : 'Plant utilisation'}
              value={u.utilisation_pct} unit="%" decimals={1}
              sub={u.class === 'vehicle'
                ? `${u.worked_days} of ${u.available_days} days worked`
                : `${fmtHours(u.used_hours)} of ${fmtHours(u.available_hours)} h worked`} />
            {u.class === 'vehicle' && (
              <StatTile label="Km driven" value={u.distance_km} decimals={0}
                sub={u.km_per_worked_day != null ? `${num(u.km_per_worked_day)} km a day worked` : undefined} />
            )}
          </>
        ) : (
          <StatTile label="Utilisation" value={t.utilisation_pct} unit="%" decimals={1} />
        )}
        <StatTile label="Engine hours" value={t.engine_hours} decimals={1} />
        <StatTile label="Productive" value={t.productive_hours} decimals={1} sub="hours" />
        <StatTile label="Standby" value={t.standby_hours} decimals={1} sub="hours" />
        <StatTile label="Breakdown" value={t.breakdown_hours} decimals={1} sub="hours"
          rag={t.breakdown_hours > 0 ? 'amber' : 'green'} />
        <StatTile label="Planned" value={t.planned_hours} decimals={1} sub="services etc." />
        <StatTile label="Weather" value={t.weather_hours} decimals={1} sub="hours" />
        <StatTile label="Tonnes moved" value={t.tonnes} />
        <StatTile label="Loads" value={t.loads} />
        <StatTile label="Breakdowns" value={t.breakdown_count}
          sub={t.avg_breakdown_hours != null ? `avg ${fmtHours(t.avg_breakdown_hours)} h` : undefined} />
      </div>

      {u && (
        <p className="small muted" style={{ margin: 'var(--sp-2) 0 0' }}>
          {u.class === 'vehicle'
            ? 'Fleet utilisation: the days it drove of the days it could (a day it drove counts as one it could).'
            : 'Plant utilisation: the hours worked of the hours available.'}
          {' '}Measured to {fmtDate(u.period.measured_to)} from {u.diary_days} shift {u.diary_days === 1 ? 'diary' : 'diaries'}
          {' '}and {u.meter_days} {u.meter_days === 1 ? 'day' : 'days'} of meter readings.
        </p>
      )}

      {data.service && (() => {
        const svcKm = data.service.meter_unit === 'km';
        const svcSfx = svcKm ? 'km' : 'h';
        const svcFmt = (x: number | null) => (svcKm ? num(x ?? 0, 0) : fmtHours(x));
        return (
        <>
          <div className="form-section">Service</div>
          <div className="issue issue--ok" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusChip tone={ragTone(data.service.status_colour)}>
              {(data.service.hours_remaining ?? 1) <= 0
                ? `${svcFmt(Math.abs(data.service.hours_remaining ?? 0))} ${svcSfx} overdue`
                : `${svcFmt(data.service.hours_remaining)} ${svcSfx} remaining`}
            </StatusChip>
            <span className="small muted">
              next at {num(data.service.next_service_hours, 0)} {svcSfx}
              {data.service.last_service_date
                ? ` · last ${fmtDate(data.service.last_service_date)}`
                : ' · no service recorded yet'}
            </span>
          </div>
        </>
        );
      })()}

      {currentLiners.length > 0 && (
        <>
          <div className="form-section">Liners in service</div>
          <table className="table">
            <thead>
              <tr>
                <th>Component</th>
                <th style={{ textAlign: 'right' }}>In service</th>
                <th style={{ textAlign: 'right' }}>Expected</th>
              </tr>
            </thead>
            <tbody>
              {currentLiners.map((l, i) => (
                <tr key={`${l.component_name ?? l.description}-${i}`}>
                  <td>{l.component_name ?? l.description}</td>
                  <td style={{ textAlign: 'right' }} className="tabular">
                    {fmtHours(l.hours_in_service)} h
                  </td>
                  <td style={{ textAlign: 'right' }} className="tabular">
                    {l.expected_life_hours ? `${num(l.expected_life_hours, 0)} h` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <div className="form-section">
        Recent days{data.daily.length > recent.length ? ` · latest ${recent.length} of ${data.daily.length}` : ''}
      </div>
      {recent.length === 0 ? (
        <p className="small muted">No diary captured for this machine in the period.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th style={{ textAlign: 'right' }}>Engine</th>
              <th style={{ textAlign: 'right' }}>Productive</th>
              <th style={{ textAlign: 'right' }}>Down</th>
              <th style={{ textAlign: 'right' }}>Avail.</th>
              <th style={{ textAlign: 'right' }}>Util.</th>
              <th style={{ textAlign: 'right' }}>Tonnes</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((d) => (
              <tr key={d.diary_date}>
                <td>{fmtDate(d.diary_date)}</td>
                <td style={{ textAlign: 'right' }} className="tabular">{fmtHours(d.engine_hours)}</td>
                <td style={{ textAlign: 'right' }} className="tabular">{fmtHours(d.productive_hours)}</td>
                <td style={{ textAlign: 'right' }} className="tabular">
                  {fmtHours(d.unplanned_downtime_hours)}
                </td>
                <td style={{ textAlign: 'right' }} className="tabular">{pct(d.availability_pct)}</td>
                <td style={{ textAlign: 'right' }} className="tabular">{pct(d.utilisation_pct)}</td>
                <td style={{ textAlign: 'right' }} className="tabular">{num(d.tonnes_effective)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
